import { useEffect, useRef, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { ThumbsUp, ThumbsDown } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Textarea } from "@/components/ui/textarea"
import { cn } from "@/lib/utils"
import {
  getStoryRating,
  setStoryRating,
  submitStoryFeedback,
  type StoryRating,
  type StoryRatingPayload,
} from "@/lib/api/feedback"

const REASONS = [
  { value: "too_hard", label: "Too difficult" },
  { value: "too_easy", label: "Too easy" },
  { value: "has_errors", label: "Had errors or typos" },
  { value: "not_interesting", label: "Not interesting" },
] as const

type Reason = (typeof REASONS)[number]["value"]

export function StoryFeedback({ storyId }: { storyId: string }) {
  const queryClient = useQueryClient()
  const queryKey = ["story-rating", storyId]

  const { data: saved } = useQuery<StoryRatingPayload | null>({
    queryKey,
    queryFn: () => getStoryRating(storyId),
  })

  const savedRating = saved?.rating ?? null

  const [formOpen, setFormOpen] = useState(false)
  const [closing, setClosing] = useState(false)
  const [reason, setReason] = useState<Reason | null>(null)
  const [text, setText] = useState("")
  const cardRef = useRef<HTMLDivElement | null>(null)

  const ratingMutation = useMutation({
    mutationFn: (rating: StoryRating) => setStoryRating(storyId, rating),
    onMutate: (rating) => {
      queryClient.setQueryData<StoryRatingPayload | null>(queryKey, () => ({
        rating,
      }))
    },
  })

  const feedbackMutation = useMutation({
    mutationFn: (payload: { reason: Reason; text: string | null }) =>
      submitStoryFeedback(storyId, payload),
  })

  const closeForm = () => {
    setClosing(true)
    window.setTimeout(() => {
      setFormOpen(false)
      setClosing(false)
    }, 200)
  }

  useEffect(() => {
    if (!formOpen) return
    const handle = (e: MouseEvent) => {
      if (!cardRef.current) return
      if (!cardRef.current.contains(e.target as Node)) closeForm()
    }
    document.addEventListener("mousedown", handle)
    return () => document.removeEventListener("mousedown", handle)
  }, [formOpen])

  const handleUp = () => {
    if (formOpen) closeForm()
    if (savedRating === "up") return
    ratingMutation.mutate("up")
  }

  const handleDown = () => {
    setReason(null)
    setText("")
    setClosing(false)
    setFormOpen(true)
    if (savedRating !== "down") ratingMutation.mutate("down")
  }

  const send = () => {
    if (!reason) return
    feedbackMutation.mutate(
      { reason, text: text.trim() || null },
      { onSuccess: () => closeForm() }
    )
  }

  return (
    <div className="relative w-full md:w-1/3">
      <div className="flex items-center gap-3">
        <span className="text-sm text-muted-foreground">
          What do you think about this story?
        </span>
        <button
          type="button"
          onClick={handleUp}
          aria-label="Helpful"
          aria-pressed={savedRating === "up"}
          className={cn(
            "flex size-8 items-center justify-center rounded-full border transition-colors",
            savedRating === "up"
              ? "border-success bg-success-soft text-success-deep"
              : "border-transparent text-muted-foreground hover:bg-muted"
          )}
        >
          <ThumbsUp className="size-4" />
        </button>
        <button
          type="button"
          onClick={handleDown}
          aria-label="Not helpful"
          aria-pressed={savedRating === "down"}
          className={cn(
            "flex size-8 items-center justify-center rounded-full border transition-colors",
            savedRating === "down"
              ? "border-destructive bg-destructive-soft text-destructive-deep"
              : "border-transparent text-muted-foreground hover:bg-muted"
          )}
        >
          <ThumbsDown className="size-4" />
        </button>
      </div>

      {formOpen && (
        <Card
          ref={cardRef}
          className={cn(
            "absolute right-0 z-10 mt-2 w-full shadow-xl duration-200",
            closing
              ? "animate-out fade-out-0 slide-out-to-top-2"
              : "animate-in fade-in-0 slide-in-from-top-2"
          )}
        >
          <CardContent className="space-y-3">
            <RadioGroup
              value={reason ?? ""}
              onValueChange={(value) => setReason(value as Reason)}
            >
              {REASONS.map((r) => (
                <label
                  key={r.value}
                  className="flex cursor-pointer items-center gap-2 text-sm"
                >
                  <RadioGroupItem value={r.value} />
                  {r.label}
                </label>
              ))}
            </RadioGroup>
            <Textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Anything else? (optional)"
              rows={2}
            />
            <div className="flex justify-end">
              <Button
                size="sm"
                onClick={send}
                disabled={!reason || feedbackMutation.isPending}
              >
                Send
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
